import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import PropTypes from 'prop-types';
import { customValidators, object, string, number as yupnumber, date, mixed, boolean } from '../../../Helpers/Validators/customValidators';
import DigitalCoverageAmounts from "../../../Helpers/Lists/CoverageAmounts/DigitalCoverageAmounts";
import { sub } from 'date-fns';
import { TextInput, SelectInput } from '../../../Helpers/Inputs';
import NumberFormatDollars from '../../../Helpers/Formatters/NumberFormatDollars';
import { SESSION_STORAGE_ACCOUNT_KEY, getSessionStorageItem, SESSION_STORAGE_CLIENTINFO_KEY } from '../../../../sessionStorage';
import TermLength from "../../../Helpers/Data/Products/Term/TermLength";
import CurrencyFormat from '../../../Helpers/Formatters/CurrencyFormat';
import TermIcon from '../../../../icons/calendar2.svg';
import SvgIcon from '@material-ui/core/SvgIcon';
import { Dialog, DialogContent, DialogTitle, IconButton, Button, Slider } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from '@material-ui/core/Box';
import BasicInfo from '../../Common/BasicInfo';
import './PredictivePath.scss';

export default function PredictiveBasicInfo(props) {

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: PredictiveBasicInfo.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'gender' });
    setValue('gender', props.formState.basicInfo.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', props.formState.basicInfo.birthdate);
    register({ name: 'height' });
    setValue('height', props.formState.basicInfo.height);
    register({ name: 'weight' });
    setValue('weight', props.formState.basicInfo.weight);
    register({ name: 'state' });
    setValue('state', props.formState.basicInfo.state);
    register({ name: 'usResident' });
    setValue('usResident', props.formState.basicInfo.usResident);
    register({ name: 'tobacco' });
    setValue('tobacco', props.formState.basicInfo.tobacco);
  }, []);

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [premiumRange, setPremiumRange] = useState(null);

  const isValidHeight = (height) => {
    if (height) {
      const feet = height.split('ft')[0].trim();
      const inches = height.split('ft')[1].split('in')[0].trim();
      if (feet > 0 && inches >= 0) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  const isValidWeight = (weight) => {
    if (weight) {
      const wt = weight.split(' ')[0].trim();
      if (wt) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  const inValidBasicInfo = () => {
    const { basicInfo } = props.formState;
    if (basicInfo === null
      || !basicInfo.gender
      || !basicInfo.birthdate
      || !isValidWeight(basicInfo.weight)
      || !isValidHeight(basicInfo.height)
      || !basicInfo.state) {
      return true;
    }
    return false;
  }

  let debounce = null;
  useEffect(() => {
    if (inValidBasicInfo()) {
      setPremiumRange(null);
      return;
    }
    else if (props.landing) {
      setPremiumRange({ higherRate: 0, lowerRate: 0 }); return;
    }
    let termLength = TermLength(props.formState.basicInfo.tobacco, props.formState.basicInfo.birthdate);
    if (!termLength) {
      termLength = "20";
    }
    props.onFieldChange("term", termLength);
  }, [props.formState.basicInfo]);

  useEffect(() => {
    const { basicInfo } = props.formState;
    if (inValidBasicInfo()
      || !props.formState.coverageAmount
      || (!props.landing && !props.formState.term)) {
      setPremiumRange(null);
      return;
    }

    let isSubscribed1 = true;
    triggerValidation(["birthdate", "height", "weight"]).then(async () => {
      if (isSubscribed1 && !props.landing) {
        await delay(1000);
        if ((Object.keys(errors).length >= 1) || !basicInfo.state || !basicInfo.birthdate || basicInfo.birthdate.indexOf('_') > -1) {
          setPremiumRange(null);
          return;
        }
        setLoading(true);
        let isSubscribed = true;
        if (debounce) {
          clearTimeout(debounce);
        }
        debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);
        return () => {
          clearTimeout(debounce);
          isSubscribed = false;
        };
      }
    });

    return () => isSubscribed1 = false;
  }, [props.formState.basicInfo, props.formState.term, props.formState.coverageAmount]);

  function delay(t, v) {
    return new Promise(function (resolve) {
      setTimeout(resolve.bind(null, v), t)
    });
  }

  const runQuote = (getIsSubscribed) => {
    setLoading(true);
    let higherRate = 0;
    let lowerRate = 0;
    const { basicInfo } = props.formState;
    const feet = basicInfo.height && basicInfo.height.split('ft')[0].trim();
    const inches = basicInfo.height && basicInfo.height.split('ft')[1].split('in')[0].trim();
    const weight = basicInfo.weight && basicInfo.weight.split(' ')[0];

    const queryParams = {
      birthdate: basicInfo.birthdate,
      state: basicInfo.state,
      gender: basicInfo.gender,
      faceAmount: props.formState.coverageAmount,
      tobacco: basicInfo.tobacco,
      healthClass: props.formState.healthClass,
      term: props.formState.term,
      heightFt: feet,
      heightIn: inches,
      weight: weight,
      oneClickAccountId: props.oneClickAccountId,
      replacePolicy: basicInfo.replacePolicy,
      noKnockouts: true,
    };

    fetch(`${covrServicesUrl}/api/MCT/GetMCTQuote`, {
      method: 'POST',
      headers: {
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(queryParams)
    })
      .then(res => res.json())
      .then(data => {
        if (getIsSubscribed()) {
          if (data && data.length > 0) {
            higherRate = Math.max.apply(Math, data.map(function (o) { return o.monthlyRate; }))
            lowerRate = Math.min.apply(Math, data.map(function (o) { return o.monthlyRate; }))
            setPremiumRange({ higherRate: higherRate, lowerRate: lowerRate });
          }
          setLoading(false);
        }
      }).catch((error) => {
        if (getIsSubscribed()) {
          ErrorHandler(error, setPremiumRange);
          setLoading(false);
        }
      })
  }

  const onFieldChange = (field, value) => {
    const basicInfo = { ...props.formState.basicInfo, [field]: value };
    props.onFieldChange('basicInfo', basicInfo);
    triggerValidation(field);
  }

  return (
    <Container>
      <div className={`${props.landing ? 'basic-info-landing' : 'predictiveForm'} `}>
        <Row>
          <Col lg={`${props.landing ? 'auto' : '6'} `}>
            <BasicInfo
              formState={props.formState.basicInfo}
              onFieldChange={(field, value) => onFieldChange(field, value)}
              hide={props.hide}
              ageSpecs={props.ageSpecs}
              product={props.product}
              showPolicyReplacementOption
              onNext={() => ""}
              errors={errors}
              register={register}
            />
            {props.landing &&
              <Row className="justify-content-center landing-button m-0">
                <Form.Group className="m-0 w-100" fullWidth>
                  <Button variant="contained" fullWidth onClick={() => props.onNext()} color="primary" disabled={!premiumRange}>
                    Personalize your quote
                </Button>
                </Form.Group>
                <Form.Group className="m-0 mb-2">
                  <Button color="primary" onClick={() => setDialogOpen(true)} disabled={!premiumRange}>SKIP HEALTH QUESTIONS</Button>
                </Form.Group>
              </Row>
            }
          </Col>
          {!props.landing &&
            <Col lg={6} className={`${props.landing ? 'd-none' : ''} `}>
              <div className="termInfo d-flex justify-content-center">
                <Form>
                  <p className="mx-auto text-center"><b>Select Amount</b></p>
                  <Form.Group>
                    <Box width="50%" className="setPosition">
                      <TextInput
                        name="coverageAmount"
                        id="coverageAmount"
                        labelAbove
                        size="small"
                        label="Select Amount"
                        value={props.formState.coverageAmount}
                        onChange={(e) => props.onFieldChange('coverageAmount', e.target.value)}
                        variant="outlined"
                        className="textInput"
                        InputProps={{
                          inputComponent: NumberFormatDollars,
                        }}
                      />
                    </Box>
                  </Form.Group>
                  <Form.Group className="ml-3 mr-4">
                    <Slider
                      min={100000}
                      max={3000000}
                      step={null}
                      value={props.formState.coverageAmount}
                      onChange={(e, value) => props.onFieldChange('coverageAmount', parseInt(value, 10))}
                      className="m-2"
                      marks={DigitalCoverageAmounts(false)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <SelectInput
                      name="term"
                      id="term"
                      value={props.formState.term}
                      onChange={(e) => props.onFieldChange('term', e.target.value)}
                      label="Years of Coverage"
                      selectOptions={[
                        { value: "10", label: "10-Year Term" },
                        { value: "15", label: "15-Year Term" },
                        { value: "20", label: "20-Year Term" },
                        { value: "25", label: "25-Year Term" },
                        { value: "30", label: "30-Year Term" },
                        { value: "35", label: "35-Year Term" },
                        { value: "40", label: "40-Year Term" },
                      ]}
                    />
                  </Form.Group>
                  <div className="content">
                    {loading ? (
                      <Row className="justify-content-center">
                        <CircularProgress />
                      </Row>
                    )
                      :
                      (
                        <div>
                          <Row>
                            <Col sm={2}>
                              <div className="term-icon">
                                <SvgIcon component={TermIcon} className="svgIcon" />
                              </div>
                            </Col>
                            <Col sm={10} className="pl-0">
                              <p className="header"><b>TERM LIFE INSURANCE</b></p>
                              <p className="subTitle">{premiumRange && props.formState.term ? `${props.formState.term} Year Term` : "Term Length"}</p>
                            </Col>
                          </Row>
                          {premiumRange ? (
                            <div>
                              <Row>
                                <Col>
                                  <p>
                                    <span className="premium">
                                      <CurrencyFormat className="quote-amount-dollars" removeSymbol number={premiumRange && premiumRange.lowerRate ? premiumRange.lowerRate : 0} /> <strong>-</strong> <CurrencyFormat className="quote-amount-dollars" removeSymbol number={premiumRange && premiumRange.higherRate ? premiumRange.higherRate : 0} />
                                    </span>
                                    <span className="quote-amount-mo"> /month</span>
                                  </p>
                                </Col>
                              </Row>
                              <div className="term-content justify-content-center">
                                <p>Now that you have an idea of how of expensive peace of mind can cost, let us go to work for you.</p>
                                <p>You're unique. Candidly answer questions for us so we can find the best available fit for you.</p>
                              </div>
                            </div>
                          ) :
                            (
                              <Row>
                                <Col>
                                  <span className="premium">--</span>
                                </Col>
                              </Row>
                            )}
                        </div>
                      )}
                  </div>
                  <Row className="justify-content-center">
                    <Form.Group>
                      <Button variant="contained" onClick={() => props.onNext()} color="primary" disabled={!premiumRange}>
                        Personalize your quote
                      </Button>
                    </Form.Group>
                  </Row>
                  <Row className="justify-content-center">
                    <Form.Group>
                      <Button color="primary" onClick={() => setDialogOpen(true)} disabled={!premiumRange}>SKIP HEALTH QUESTIONS</Button>
                    </Form.Group>
                  </Row>
                </Form>
              </div>
            </Col>
          }

        </Row>
      </div>

      <ModalPopup
        open={dialogOpen}
        onDialogClosed={() => {
          setDialogOpen(false);
        }}
        onNext={() => props.onNext()}
        redirect={() => props.redirectToQuoter()}
      />
    </Container>
  )
}

function ModalPopup(props) {
  return (
    <div>
      <Dialog maxWidth="sm" className="help-dialog" open={props.open} onClose={props.onDialogClosed} scroll="body">
        <DialogTitle className="dialogTitle">Why personalize your health class ?</DialogTitle>
        <DialogContent className="dialogContent">
          <IconButton className="product-info-close-button" aria-label="close" onClick={props.onDialogClosed}>
            <CloseIcon />
          </IconButton>
          <div className="justify-content-center">
            <p>Each insurance company has their criteria for deciding how your health affects rates. By choosing not to personlize your quote and answer some health questions, you run an increased
            risk that you may not be eliginble for the quoted health class and rates.
            </p>
          </div>
          <Row className="justify-content-center">
            <Form.Group>
              <Button variant="contained" onClick={props.onNext} color="primary">
                Personalize your quote
              </Button>
            </Form.Group>
          </Row>
          <Row className="justify-content-center">
            <Form.Group>
              <Button color="primary" onClick={props.redirect}>SKIP HEALTH QUESTIONS</Button>
            </Form.Group>
          </Row>
        </DialogContent>
      </Dialog>
    </div>
  );
}

PredictiveBasicInfo.propTypes = {
  onFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  ageSpecs: PropTypes.object.isRequired,
  queryString: PropTypes.object,
}

PredictiveBasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    basicInfo: object().shape({
      require: boolean(),
      gender: string().required('Gender is required.'),
      birthdate: date()
        .transform(emptyStringToUndefined)
        .typeError('Invalid date.')
        .max(sub(new Date(), { years: ageSpecs.min }), `Minimum age is ${ageSpecs.min}.`)
        .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
        .required('Birthdate is required.'),
      state: string().required('State is required.'),
      height: customValidators.string()
        .required('Height is required.')
        .isValidHeightInchString('Invalid height', 56, 79),
      weight: customValidators.string()
        .required('Weight is required.')
        .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
      email: string().notRequired().email('Invalid email'),
    })
  })
};

PredictiveBasicInfo.DefaultFormState = (prefill = false, basic = null) => {
  return {
    basicInfo: {
      phone: prefill ? "1234567890" : (basic && basic.phone) || "",
      gender: prefill ? "M" : (basic && basic.gender) || "",
      birthdate: prefill ? "11/15/1970" : (basic && basic.birthdate) || "",
      tobacco: prefill ? "false" : (basic && basic.tobacco) || "false",
      state: prefill ? "ID" : (basic && basic.state) || "",
      height: prefill ? "5 ft 10 in" : (basic && basic.height) || "",
      weight: prefill ? "175 pounds" : (basic && basic.weight) || "",
      email: prefill ? "test@gmail.com" : (basic && basic.email) || "",
      usResident: prefill ? true : (basic && basic.usResident) || false,
      replacePolicy: prefill ? false : (basic && basic.replacePolicy) || false,
      bmi: null,
    },
    coverageAmount: (basic && basic.faceAmount) || 500000,
    healthClass: (basic && basic.healthClass) || "P",
    term: prefill ? "20" : (basic && basic.termLength) || "",
    isAccelerated: false,
  }
}



// WEBPACK FOOTER //
// ./src/components/Quoters/PreQuote/steps/PredictiveBasicInfo.js